// dialog.ts — the Bridge home's seam onto THE modal host (seam dialog-system;
// R1-R5, R62 — the contract below is unchanged, only its HOME moved).
//
// This file used to BE the engine. Styles x Themes PR-3b promoted it into the
// shared pi-stack client layer (pi-stack/src/client/modal-host.ts) because
// the Helm was running SIX hand-rolled hosts beside it, each with a different
// subset of Escape / scrim / Tab-trap / focus-return / two-edge motion. One
// engine per surface is two conventions; one engine, two CSS vocabularies, is
// the same machine everywhere. The confirm-destructive DIALOG went with it,
// into pi-stack/src/client/confirm.ts, where it now sits beside the inline
// arm-then-fire tier so the product has ONE answer to "how do we ask before
// destroying something".
//
// Nothing about the console's contract changed. Every name below is the name
// it was, `data-act` stays 'yes'/'no'/'ok' (home-grid.spec.mjs,
// admin-users.spec.mjs and home-menu-probes.page.js address buttons by those
// hooks), and dialog.test.mjs still bundles THIS path and drives the real
// engine over its stub document.
//
//   openDialog(host, build, opts?)   custom content (forms, pickers, detail)
//   confirmDialog(host, spec)        the confirmation LAYOUT SPEC
//   noticeDialog(host, spec)         single-acknowledgment outcome dialogs
//   showDialogMsg(el, text)          error/refusal text with an animated
//                                    entrance (re-fires per message)
//   destroyDialog(host)              instant teardown for route unmounts
//   stackHostFor(base)               the one stacked sibling overlay per base
//
// THE CONFIRMATION LAYOUT SPEC (R1/R5 — porting the old bare `<p>` + two
// identical buttons verbatim FAILS the seam) is enforced by confirmVm in
// pi-stack/src/client/confirm.ts:
//   - a title line naming the decision;
//   - body copy naming the CONSEQUENCE ("this aborts X and stops its VM");
//   - actions right-aligned, Cancel first in DOM, the PRIMARY action
//     rightmost and NAMED with the verb — since PR-2 the verb is the tooltip
//     + aria-label of an icon-only .ibtn, not button text;
//   - the destructive emphasis lands on the PRIMARY action, never on Cancel.

import {
  openModal, noticeModal, showModalMsg, destroyModal,
  stackHostFor as stackHostForVocab, HC_VOCAB,
  type ModalHandle, type ModalOptions, type NoticeSpec,
} from 'pi-stack/client/modal-host.js';
import { confirmModal, type ConfirmSpec } from 'pi-stack/client/confirm.js';

export type DialogOptions = ModalOptions;
export type DialogHandle = ModalHandle;
export type { ConfirmSpec, NoticeSpec };
export type { ConfirmButtonVm } from 'pi-stack/client/confirm.js';

export { FOCUSABLE_SELECTOR, tabTrapTarget, openModalCount } from 'pi-stack/client/modal-host.js';
export { confirmVm } from 'pi-stack/client/confirm.js';

/** The stacked overlay host ABOVE `base` — lazily created once per base and
 * reused. Only the TOPMOST open host acts on Escape/Tab, which is the exact
 * double-fire that forced the old content-swap browse design (D7). */
export function stackHostFor(base: HTMLElement): HTMLElement {
  return stackHostForVocab(base, HC_VOCAB);
}

/** Open a dialog on `host` (the .hc-modal overlay). Opening while one is
 * already showing swaps the content in place. */
export function openDialog(
  host: HTMLElement,
  build: (body: HTMLElement, close: () => void) => void,
  opts: DialogOptions = {},
): DialogHandle {
  return openModal(host, build, opts, HC_VOCAB);
}

/** A confirmation dialog per the layout spec. Confirm LEAVES NOW: the primary
 * goes busy, the exit starts, and onConfirm runs behind the close. */
export function confirmDialog(host: HTMLElement, spec: ConfirmSpec): DialogHandle {
  return confirmModal(host, spec, HC_VOCAB);
}

/** A single-acknowledgment notice. */
export function noticeDialog(host: HTMLElement, spec: NoticeSpec): DialogHandle {
  return noticeModal(host, spec, HC_VOCAB);
}

/** Set an error/refusal message with an animated entrance — re-fired per
 * message so a second refusal visibly ARRIVES. */
export function showDialogMsg(el: HTMLElement, text: string): void {
  showModalMsg(el, text, HC_VOCAB.msgIn);
}

/** Instant teardown for route unmounts: no exit animation, listeners gone,
 * focus NOT restored (the page is leaving). */
export function destroyDialog(host: HTMLElement): void {
  destroyModal(host);
}
